import { useEffect, useState } from "react";
import AppShell from "../../components/AppShell";
import { PageHeader, Panel, StatusPill, EmptyState } from "../../components/Bits";
import { Modal } from "./Students";
import { api } from "../../lib/api";
import { toast } from "sonner";

export default function AdminLeaves() {
  const [rows, setRows] = useState(null);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [note, setNote] = useState("");

  async function load() { const { data } = await api.get("/leaves"); setRows(data); }
  useEffect(() => { load(); }, []);

  async function decide(status) {
    await api.put(`/admin/leaves/${active.id}`, { status, admin_note: note });
    toast.success(`Application ${status}`);
    setActive(null); setNote(""); load();
  }

  const filtered = (rows || []).filter((l) => filter === "all" ? true : l.status === filter);

  return (
    <AppShell>
      <PageHeader overline="Permissions" title="Leave & day-out applications" subtitle="Approve or reject resident leave requests." />
      <Panel
        title={`${filtered.length} ${filtered.length === 1 ? "application" : "applications"}`}
        action={
          <div className="flex gap-1 bg-[#F4F1EA] rounded-md p-1 text-sm">
            {["all", "pending", "approved", "rejected"].map((k) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`leave-filter-${k}`} className={`px-3 py-1 rounded-md capitalize ${filter === k ? "bg-white text-deep shadow-sm" : "text-warm"}`}>{k}</button>
            ))}
          </div>
        }
      >
        {rows === null ? <div className="text-sm text-warm">Loading…</div>
          : filtered.length === 0 ? <EmptyState title="No applications" />
            : (
              <div className="overflow-x-auto -mx-6">
                <table className="min-w-full text-sm">
                  <thead><tr className="bg-[#F4F1EA] text-warm uppercase tracking-wider text-xs">
                    <th className="text-left px-6 py-3">Resident</th>
                    <th className="text-left px-6 py-3">Type</th>
                    <th className="text-left px-6 py-3">Dates</th>
                    <th className="text-left px-6 py-3">Reason</th>
                    <th className="text-left px-6 py-3">Status</th>
                    <th className="text-right px-6 py-3">Action</th>
                  </tr></thead>
                  <tbody>
                    {filtered.map((l) => (
                      <tr key={l.id} className="border-t border-warm hover:bg-[#FDFBF7]" data-testid={`leave-row-${l.id}`}>
                        <td className="px-6 py-3 font-medium text-deep">{l.student_name}</td>
                        <td className="px-6 py-3 capitalize">{l.type.replace("_", " ")}</td>
                        <td className="px-6 py-3">{l.from_date} → {l.to_date}</td>
                        <td className="px-6 py-3 max-w-xs truncate">{l.reason}</td>
                        <td className="px-6 py-3"><StatusPill status={l.status} /></td>
                        <td className="px-6 py-3 text-right">
                          <button onClick={() => { setActive(l); setNote(l.admin_note || ""); }} data-testid={`review-leave-${l.id}`} className="text-sm text-[#C86F58] hover:underline">Review</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </Panel>

      {active && (
        <Modal title="Review application" onClose={() => setActive(null)}>
          <div className="space-y-3 text-sm mb-4">
            <Row k="Resident" v={active.student_name} />
            <Row k="Type" v={<span className="capitalize">{active.type.replace("_", " ")}</span>} />
            <Row k="From → To" v={`${active.from_date} → ${active.to_date}`} />
            {active.destination && <Row k="Destination" v={active.destination} />}
            {active.contact_during_leave && <Row k="Contact during leave" v={active.contact_during_leave} />}
            <Row k="Reason" v={<span className="whitespace-pre-wrap">{active.reason}</span>} />
            <Row k="Status" v={<StatusPill status={active.status} />} />
          </div>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-deep font-medium mb-1.5 block">Admin note</span>
            <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} className="w-full px-3 py-2 rounded-md border border-warm bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#E8C2B8]" />
          </label>
          <div className="flex justify-end gap-2 mt-4">
            <button onClick={() => decide("rejected")} data-testid="reject-leave-btn" className="px-5 py-2 rounded-md text-sm border border-[#D46B6B] text-[#8C3F3F] hover:bg-[#F5D8D8]">Reject</button>
            <button onClick={() => decide("approved")} data-testid="approve-leave-btn" className="bg-[#9CAF88] text-white px-6 py-2 rounded-md text-sm hover:bg-[#869971]">Approve</button>
          </div>
        </Modal>
      )}
    </AppShell>
  );
}

function Row({ k, v }) {
  return (
    <div className="grid grid-cols-3 gap-4">
      <div className="text-xs uppercase tracking-wider text-warm">{k}</div>
      <div className="col-span-2 text-deep">{v}</div>
    </div>
  );
}
